import React, { useEffect, useState } from "react";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { useAuth } from "../../../context/auth-context";
import { rootUrl } from "../../../helpers";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { FaPlus, FaTimes, FaChartBar, FaFileAlt } from "react-icons/fa";
import { IoIosSwap } from "react-icons/io";
import { customStyles } from "../../../styles";

const Journal = ({ client }) => {
  const [journalData, setJournalData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showFormImprest, setShowFormImprest] = useState(false);
  const [selType, setSelType] = useState('322020');
  const [difference, setDifference] = useState(0);
  const [activeTab, setActiveTab] = useState('entries'); // Add tab state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredJournalData, setFilteredJournalData] = useState([]);
  const [formData, setFormData] = useState({
    fromcoa: "",
    tocoa: "",
    amount: "",
    desc: "",
    date: "",
  });

  const auth = useAuth();

  const {allData, setAllData} = useOutletContext();

  const [formDataImprest, setFormDataImprest] = useState({
    fromcoa: "",
    tocoa: "",
    amount: "",
    desc: "",
    date: "",
    bank: "",
    employee: "",
  });

  useEffect(()=>{
    setDifference(allData.budget.find(a=>a.id == formDataImprest.tocoa)?.coa)
  },[formDataImprest.tocoa])

  // Filter journal data based on date range
  useEffect(() => {
    if (startDate && endDate) {
      const filtered = journalData.filter(entry => {
        const entryDate = new Date(entry.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return entryDate >= start && entryDate <= end;
      });
      setFilteredJournalData(filtered);
    } else {
      setFilteredJournalData(journalData);
    }
  }, [journalData, startDate, endDate]);

  const fetchJournalData = async () => {
    try {
      const response = await axios.get(rootUrl(`/getJournal/${auth.user.id}`));
      setJournalData(response.data.data);
    } catch (error) {
      console.error("Error fetching journal data:", error);
    }
  };
  
  useEffect(() => {
    fetchJournalData();
  }, []);
  
  const handleFormSubmit = async () => {
    try {
      const updatedFormData = { ...formData, uid: auth.user.id };
  
      const response = await axios.post(rootUrl(`/addJournal`), updatedFormData, {
        headers: { "Content-Type": "application/json" },
      });
  
      if (response.data.status === 200) {
        toast.success("Sucessful")
        setShowForm(false);
        fetchJournalData(); 
      } else {
        toast.error("Something went wrong")
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }

  const handleFormSubmitImprest = async () => {
    formDataImprest['fromcoa'] = allData && allData.budget.find(a=>a.coa == selType)?.id
    try {
      const updatedFormData = { ...formDataImprest, uid: auth.user.id };
  
      const response = await axios.post(rootUrl(`/addJournal`), updatedFormData, {
        headers: { "Content-Type": "application/json" },
      });
  
      if (response.data.status === 200) {
        toast.success("Sucessful")
        setShowFormImprest(false);
        fetchJournalData(); 
      } else {
        toast.error("Something went wrong")
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }

  // Calculate journal statistics
  const calculateJournalStats = () => {
    const data = filteredJournalData;
    const totalAmount = data.reduce((sum, entry) => sum + parseFloat(entry.amount), 0);
    const totalEntries = data.length;
    
    // Group by COA
    const coaSummary = data.reduce((acc, entry) => {
      const fromCoa = allData.budget?.find((a) => a.id === entry.from_coa)?.coa;
      const toCoa = allData.budget?.find((a) => a.id === entry.to_coa)?.coa;
      
      if (!acc[fromCoa]) acc[fromCoa] = { total: 0, count: 0 };
      if (!acc[toCoa]) acc[toCoa] = { total: 0, count: 0 };
      
      acc[fromCoa].total += parseFloat(entry.amount);
      acc[fromCoa].count += 1;
      acc[toCoa].total += parseFloat(entry.amount);
      acc[toCoa].count += 1;
      
      return acc;
    }, {});

    // Monthly summary
    const monthlySummary = data.reduce((acc, entry) => {
      const month = new Date(entry.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!acc[month]) acc[month] = { total: 0, count: 0 };
      acc[month].total += parseFloat(entry.amount);
      acc[month].count += 1;
      return acc;
    }, {});

    return {
      totalAmount,
      totalEntries,
      coaSummary,
      monthlySummary
    };
  };

  const stats = calculateJournalStats();

  const columns = [
    {
      name: "Sn",
      selector: (_, index) => index + 1,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.date.substring(0, 10),
      sortable: true,
    },
    {
      name: "From",
      selector: (row) =>
        allData.budget?.find((a) => a.id === row.from_coa)?.coa 
        +": "+
        allData.coa?.find(b=>b.account === allData.budget?.find((a) => a.id === row.from_coa)?.coa)?.name || "",
    },
    {
      name: "To",
      selector: (row) =>
        allData.budget?.find((a) => a.id === row.to_coa)?.coa 
        +": "+
        allData.coa?.find(b=>b.account === allData.budget?.find((a) => a.id === row.to_coa)?.coa)?.name || "",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
    },
  ];

  // Report columns for COA summary
  const coaReportColumns = [
    {
      name: "COA Code",
      selector: (row) => row.coa,
      sortable: true,
    },
    {
      name: "Account Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.total.toFixed(2),
      sortable: true,
    },
    {
      name: "Number of Entries",
      selector: (row) => row.count,
      sortable: true,
    },
  ];

  // Report columns for monthly summary
  const monthlyReportColumns = [
    {
      name: "Month",
      selector: (row) => row.month,
      sortable: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.total.toFixed(2),
      sortable: true,
    },
    {
      name: "Number of Entries",
      selector: (row) => row.count,
      sortable: true,
    },
  ];

  // Prepare COA summary data for DataTable
  const coaReportData = Object.entries(stats.coaSummary).map(([coa, data]) => ({
    coa,
    name: allData.coa?.find(a => a.account === coa)?.name || 'Unknown',
    total: data.total,
    count: data.count,
  }));

  // Prepare monthly summary data for DataTable
  const monthlyReportData = Object.entries(stats.monthlySummary).map(([month, data]) => ({
    month,
    total: data.total,
    count: data.count,
  }));

  return (
    <div className="w-full bg-white min-h-[400px] rounded-md mt-10 p-5">
      <div className="flex justify-between">
        <h1 className="font-bold text-2xl flex items-center">
          <IoIosSwap className="mr-2" /> Journal
        </h1>
        {activeTab === 'entries' && (
          <div className="flex">
            <button
              onClick={() => setShowFormImprest(true)}
              className="flex btn bg-blue-950 text-white items-center justify-center border border-gray-300 h-[40px] px-3 ml-2 rounded-md text-[12px]"
            >
              <FaPlus/>
              Imprest Retirement
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex btn bg-blue-950 text-white items-center justify-center border border-gray-300 h-[40px] px-3 ml-2 rounded-md text-[12px]"
            >
              <FaPlus/>
              Add Transaction
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b mt-4">
        <button
          className={`py-2 px-4 ${activeTab === 'entries' ? 'border-b-2 border-blue-500 text-blue-500 font-semibold' : 'text-gray-600'}`}
          onClick={() => setActiveTab('entries')}
        >
          <IoIosSwap className="inline mr-2" /> Journal Entries
        </button>
        <button
          className={`py-2 px-4 ${activeTab === 'reports' ? 'border-b-2 border-blue-500 text-blue-500 font-semibold' : 'text-gray-600'}`}
          onClick={() => setActiveTab('reports')}
        >
          <FaChartBar className="inline mr-2" /> Journal Reports
        </button>
      </div>

      <hr className="my-2" />

      {/* Journal Entries Tab */}
      {activeTab === 'entries' && (
        <>
          <span className='text-gray-800 pt-5 text-small'>A list of all journal entries below</span>
          <DataTable
            columns={columns}
            data={filteredJournalData}
            pagination
            responsive
            highlightOnHover
            customStyles={customStyles}
          />
        </>
      )}

      {/* Journal Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Date Range Filter */}
          <div className="flex gap-4 items-center">
            <div>
              <label className="text-sm font-medium text-gray-700">From Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="ml-2 p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">To Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="ml-2 p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border">
              <h3 className="text-lg font-semibold text-blue-800">Total Entries</h3>
              <p className="text-2xl font-bold text-blue-600">{stats.totalEntries}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border">
              <h3 className="text-lg font-semibold text-blue-800">Total Amount</h3>
              <p className="text-2xl font-bold text-blue-600">K{stats.totalAmount.toFixed(2)}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border">
              <h3 className="text-lg font-semibold text-purple-800">Date Range</h3>
              <p className="text-sm text-purple-600">
                {startDate && endDate ? `${startDate} to ${endDate}` : 'All dates'}
              </p>
            </div>
          </div>

          {/* COA Summary Report */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FaFileAlt className="mr-2" /> COA Summary Report
            </h3>
            <DataTable
              columns={coaReportColumns}
              data={coaReportData}
              pagination
              responsive
              highlightOnHover
              customStyles={customStyles}
            />
          </div>

          {/* Monthly Summary Report */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <FaChartBar className="mr-2" /> Monthly Summary Report
            </h3>
            <DataTable
              columns={monthlyReportColumns}
              data={monthlyReportData}
              pagination
              responsive
              highlightOnHover
              customStyles={customStyles}
            />
          </div>
        </div>
      )}

      {/* Add Transaction Form */}
      {showForm && (
        <div
          className="bg-white fixed top-0 right-0 h-full w-1/4 overflow-hidden shadow-lg"
          style={{ display: showForm ? "block" : "none" }}
        >
          <FaTimes
            onClick={() => setShowForm(false)}
            className="text-lg text-red-500 float-right m-2" />
          <h3 className="m-2 font-semibold text-lg">Journal</h3>
          <hr />
          <div className="max-w-md mx-auto bg-white p-8 rounded">
            <Select
              id="budget"
              options={allData.budget.map((res) => ({
                value: res.id,
                label: `${res.coa}: ${allData.coa?.find(a=>a.account === res.coa)?.name}`,
              }))}
              placeholder="From COA"
              onChange={(selected) =>
                setFormData({ ...formData, fromcoa: selected.value })
              }
              className="border rounded p-2 w-full mb-4"
            />
            <Select
              id="coa"
              options={allData.budget.map((res) => ({
                value: res.id,
                label: `${res.coa}: ${allData.coa?.find(a=>a.account === res.coa)?.name}`,
              }))}
              placeholder="To COA"
              onChange={(selected) =>
                setFormData({ ...formData, tocoa: selected.value })
              }
              className="border rounded p-2 w-full mb-4"
            />
            <input
              id="amount"
              type="text"
              placeholder="Amount"
              className="border rounded p-2 w-full mb-4"
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
            />
            <input
              id="date"
              type="date"
              placeholder="date"
              className="border rounded p-2 w-full mb-4"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
            />
            <textarea
              id="desc"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
              placeholder="Description"
              value={formData.desc}
              onChange={(e) =>
                setFormData({ ...formData, desc: e.target.value })
              }
            />
            <div className="text-center">
              <button
                onClick={handleFormSubmit}
                className="mt-6 bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline-blue active:bg-blue-800"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Imprest Form */}
      {showFormImprest && (
        <div
          className="bg-white fixed top-0 right-0 h-full w-1/4 overflow-hidden shadow-lg"
          style={{ display: showFormImprest ? "block" : "none" }}
        >
          <FaTimes
            onClick={() => setShowFormImprest(false)}
            className="text-lg text-red-500 float-right m-2" />
          <h3 className="m-2 font-semibold text-lg">Imprest</h3>
          <hr />
          <div className="max-w-md mx-auto bg-white p-8 rounded">

          <Select
              id="budget"
              options={[{
                label:'Imprest',
                value:'322020'
              },
              {
                label:'Advance',
                value:'322070'
              }]}
              placeholder="Type"
              onChange={(selected) =>
                setSelType(selected.value)
              }
              className="border rounded p-2 w-full mb-4"
            />
        
            <Select
              id="budget"
              options={allData.budget.map((res) => ({
                value: res.id,
                label: `${res.coa}: ${allData.coa?.find(a=>a.account === res.coa)?.name}`,
              }))}
              placeholder="COA"
              onChange={(selected) =>
                setFormDataImprest({ ...formDataImprest, tocoa: selected.value })
              }
              className="border rounded p-2 w-full mb-4"
            />

            <Select
              id="budget"
              options={allData.suppliers.filter(a=>a.type == 2).map((res) => ({
                value: res.id,
                label: `${res.name}`,
              }))}
              placeholder="Select Employee"
              onChange={(selected) =>
                setFormDataImprest({ ...formDataImprest, employee: selected.value })
              }
              className="border rounded p-2 w-full mb-4"
            />  
            
            <input
              id="amount"
              type="text"
              placeholder="Amount"
              className="border rounded p-2 w-full mb-4"
              value={formDataImprest.amount}
              onChange={(e) =>
                setFormDataImprest({ ...formDataImprest, amount: e.target.value })
              }
            />
  
        {
           difference == '326040' ?  <Select
          id="budget"
          options={allData.bank.map((res) => ({
            value: res.id,
            label: `${res.name}`,
          }))}
          placeholder="Select Cashbook"
          onChange={(selected) =>
            setFormDataImprest({ ...formDataImprest, bank: selected.value })
          }
          className="border rounded p-2 w-full mb-4"
        />  
        :null
        }
    

            <input
              id="date"
              type="date"
              placeholder="date"
              className="border rounded p-2 w-full mb-4"
              value={formDataImprest.date}
              onChange={(e) =>
                setFormDataImprest({ ...formDataImprest, date: e.target.value })
              }
            />
            <textarea
              id="desc"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
              placeholder="Description"
              value={formDataImprest.desc}
              onChange={(e) =>
                setFormDataImprest({ ...formDataImprest, desc: e.target.value })
              }
            />
            <div className="text-center">
              <button
                onClick={handleFormSubmitImprest}
                className="mt-6 bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline-blue active:bg-blue-800"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journal;
